import { computed, ref, shallowRef } from 'vue'
import * as THREE from 'three'
import type { SelectableHint } from '@/composables/useMeshHighlighting'
import type { SelectionHighlights, SelectionHintInstances } from '@/composables/useViewerSelection'
import { instanceCountOf } from '@/utils/instanceAllocation'

export type InstancePickingDeps = {
  hintInstances: SelectionHintInstances
  highlights: SelectionHighlights
  requestRender: () => void
}

export function useInstancePicking(deps: InstancePickingDeps) {
  const target = shallowRef<SelectableHint | null>(null)
  const required = ref(0)
  const picked = ref<number[]>([])

  const active = computed(() => !!target.value)
  const canSave = computed(() => active.value && picked.value.length === required.value)

  function start(hint: SelectableHint, requiredCount: number, initial: number[] = []) {
    target.value = hint
    required.value = requiredCount
    picked.value = [...new Set(initial)]
    render()
  }

  function stop() {
    if (!target.value) return
    target.value = null
    required.value = 0
    picked.value = []
    deps.highlights.clearSelectionHighlight()
    deps.requestRender()
  }

  function instanceAt(mesh: THREE.Mesh): number | null {
    const hint = target.value
    if (!hint) return null
    const index = deps.hintInstances.instanceOf(hint, mesh)
    return index !== null && index < instanceCountOf(hint.milliQuantity) ? index : null
  }

  function isPickable(mesh: THREE.Mesh): boolean {
    return instanceAt(mesh) !== null
  }

  function hoverGroup(mesh: THREE.Mesh): THREE.Mesh[] {
    const index = instanceAt(mesh)
    if (index === null || !target.value) return []
    return deps.hintInstances.instanceMeshes(target.value, index).filter((m) => m.visible)
  }

  function toggle(mesh: THREE.Mesh | null) {
    const index = mesh ? instanceAt(mesh) : null
    if (index === null) return
    picked.value = picked.value.includes(index)
      ? picked.value.filter((i) => i !== index)
      : [...picked.value, index]
    render()
  }

  function pickedMeshes(): THREE.Mesh[] {
    const hint = target.value
    if (!hint) return []
    return picked.value
      .flatMap((index) => deps.hintInstances.instanceMeshes(hint, index))
      .filter((m) => m.visible)
  }

  function render() {
    deps.highlights.clearHover()
    deps.highlights.applySelectionHighlight(pickedMeshes())
    deps.requestRender()
  }

  return {
    target,
    required,
    picked,
    active,
    canSave,
    start,
    stop,
    isPickable,
    hoverGroup,
    toggle,
    render,
  }
}
